import { Mission, MissionStatus } from '../../../models/mission.model';
import { isMissionStatus, normalizeMissionPayload } from '../../../../common/utils/mission';

export interface MissionPublicationData {
  event_type?: string;
  mission_id?: string;
  occured_at?: number;
  occurred_at?: number;
  new_status?: string;
  status?: string;
  data?: unknown;
  detail?: unknown;
  mission?: unknown;
}

export interface MissionChangePublication {
  mission_id: string;
  newStatus?: MissionStatus;
  time?: number;
  eventType: string;
  mission?: Mission;
  comment?: string;
}

export type MappedMissionPublication =
  | {
      type: 'new';
      mission?: Mission;
    }
  | {
      type: 'changed';
      change: MissionChangePublication;
    };

const missionStatusByEvent: Readonly<Record<string, MissionStatus>> = {
  started: MissionStatus.Active,
  arrived_on_checkpoint: MissionStatus.Active,
  checkpoint_completed: MissionStatus.Active,
  report_sent: MissionStatus.AwaitingReview,
  report_sent_for_rework: MissionStatus.Rework,
  completed: MissionStatus.Completed,
  closed: MissionStatus.Closed,
};

const missionRefreshEvents = new Set(['checkpoints_updated']);

export function mapMissionPublication(value: unknown): MappedMissionPublication | undefined {
  const publicationRecord = asRecord(value);

  if (!publicationRecord) {
    return undefined;
  }

  const publication = publicationRecord as MissionPublicationData;
  const eventType = getString(publication.event_type);

  if (!eventType) {
    return undefined;
  }

  const details = getMissionEventDetails(publication);
  const mission = extractMissionFromPublication(publication, details);

  if (
    eventType === MissionStatus.New ||
    (eventType === 'created' && (!mission || mission.current_status === MissionStatus.New))
  ) {
    return {
      type: 'new',
      mission,
    };
  }

  const missionId = getString(publication.mission_id) || mission?.id;

  if (!missionId) {
    return undefined;
  }

  const newStatus = resolveMissionStatus(publication, mission);

  if (!newStatus && !missionRefreshEvents.has(eventType)) {
    return undefined;
  }

  const time = publication.occured_at ?? publication.occurred_at;
  const comment = getString(details?.['comment']) || getString(details?.['dispatcher_commentary']);
  const isCheckpointEvent =
    eventType === 'arrived_on_checkpoint' || eventType === 'checkpoint_completed';

  return {
    type: 'changed',
    change: {
      mission_id: missionId,
      eventType,
      mission,
      ...(newStatus ? { newStatus } : {}),
      ...(isCheckpointEvent && time !== undefined ? { time } : {}),
      ...(comment ? { comment } : {}),
    },
  };
}

function resolveMissionStatus(
  publication: MissionPublicationData,
  mission?: Mission,
): MissionStatus | undefined {
  const eventType = publication.event_type || '';
  const mappedStatus = missionStatusByEvent[eventType];

  if (mappedStatus) {
    return mappedStatus;
  }

  if (isMissionStatus(eventType)) {
    return eventType;
  }

  const explicitStatus = publication.new_status || publication.status;

  if (isMissionStatus(explicitStatus)) {
    return explicitStatus;
  }

  return mission?.current_status;
}

function getMissionEventDetails(
  publication: MissionPublicationData,
): Record<string, unknown> | undefined {
  return asRecord(publication.data) || asRecord(publication.detail);
}

function extractMissionFromPublication(
  publication: MissionPublicationData,
  details?: Record<string, unknown>,
): Mission | undefined {
  const candidates = [publication.mission, details?.['mission'], details];

  for (const candidate of candidates) {
    const mission = normalizeMissionPayload(candidate);

    if (mission) {
      return mission;
    }
  }

  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}
